import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { useVesselStore } from '../../../context/VesselContext';
import { NoonReportFreshWaterSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { NoonReportSectionEnum, NoonReportTypeEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { ValidatedNoonReport } from '../../../types/ViewModel/ValidatedNoonReport';
import { createSectionData, getNumberValue, hasMissingValues, hasInvalidFields } from '../../../utils/aolFieldsHelper';
import { ErrorText } from '../../shared/error-text/ErrorText.style';
import { ListHeaderStyle } from '../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { EngineListWrapper, EngineListRow, EngineListHeader, EngineListValuesWrapper } from '../engines-section/EnginesSection.style';
import { FreshWaterWrapper } from './FreshWaterSection.style';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import { isNullOrUndefined } from 'src/utils/objectUtils';

type FreshWaterVM = {
    freshWaterDeliveredFromShore?: number;
    freshWaterEvaporatorRunningHours1?: number;
    freshWaterEvaporatorRunningHours2?: number;
    freshWaterEvaporatorProduction1?: number;
    freshWaterEvaporatorProduction2?: number;
    freshWaterProductionByReversedOsmosis?: number;
    freshWaterUsedForTankCleaning?: number;
    freshWaterUsedForDomesticUse?: number;
    freshWaterProducedBySteam?: number;
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

const FreshWaterSectionComponent: React.FC = () => {
    const { vessel } = useVesselStore();
    const { selectedAolGroup, selectedAolDataItem, aolSyncEvent } = useAolStore();
    const [vm, setVM] = useState<FreshWaterVM>({});
    const [canProduceFreshWaterWithSteam, setCanProduceFreshWaterWithSteam] = useState(false);
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const [canProduceFreshWaterByOsmosis, setCanProduceFreshWaterByOsmosis] = useState<boolean>(false);
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (!selectedAolDataItem || !vessel?.vesselConfigurationV3) return;

        if (!vm.isInitialized) {
            initVM();
        }
    }, [selectedAolDataItem, vessel]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.FreshWater &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (aolSyncEvent && aolSyncEvent.noonReport && aolSyncEvent.type === 'sync_hours') {
            initVM();
        }
    }, [aolSyncEvent]);

    const isReadOnly = checkIsAolReadOnly(vm?.sectionData);

    const initVM = () => {
        if (!selectedAolDataItem) return;
        const noonReport = selectedAolDataItem.noonReport;
        const freshWaterVM: FreshWaterVM = {
            freshWaterDeliveredFromShore: getNumberValue(noonReport?.freshWaterSection?.freshWaterDeliveredFromShore, 2, true),
            freshWaterEvaporatorRunningHours1: getEvaporatorData(noonReport, 0, 'runningHours'),
            freshWaterEvaporatorRunningHours2: getEvaporatorData(noonReport, 1, 'runningHours'),
            freshWaterEvaporatorProduction1: getEvaporatorData(noonReport, 0, 'production'),
            freshWaterEvaporatorProduction2: getEvaporatorData(noonReport, 1, 'production'),
            freshWaterProductionByReversedOsmosis: getNumberValue(noonReport?.freshWaterSection?.freshWaterProductionByReversedOsmosis, 2, true),
            freshWaterUsedForTankCleaning: getNumberValue(noonReport?.freshWaterSection?.freshWaterUsedForTankCleaning, 0, true),
            freshWaterUsedForDomesticUse: getNumberValue(noonReport?.freshWaterSection?.freshWaterUsedForDomesticUse, 0, true),
            freshWaterProducedBySteam: getNumberValue(noonReport?.freshWaterSection?.freshWaterProducedBySteam, 2, true),
            sectionData: noonReport?.freshWaterSection?.sectionData ?? createSectionData(NoonReportSectionEnum.FreshWater),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
        };

        setCanProduceFreshWaterByOsmosis(vessel?.vesselConfigurationV3?.canProduceFreshWaterByOsmosis ?? false);
        setCanProduceFreshWaterWithSteam(vessel?.vesselConfigurationV3?.canProduceFreshWaterWithSteam ?? false);
        setHasEmptyValues(false);
        setVM({ ...freshWaterVM });
    };

    const getEvaporatorData = (noonReport: ValidatedNoonReport | undefined, index: number, selector: string) => {
        if (isNullOrUndefined(noonReport?.freshWaterSection)) return 0;
        if (noonReport?.freshWaterSection?.freshWaterEvaporatorData[index] === undefined) return 0;
        return noonReport?.freshWaterSection?.freshWaterEvaporatorData[index][selector];
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        vm[name] = value ? +value : undefined;

        hasInvalidFields(vm, name, changeObject.isValid);
        setVM({ ...vm });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;

        hasInvalidFields(vm, changeObject.name, changeObject.isValid);
        setVM({ ...vm });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const save = () => {
        const ignoreList: string[] = [];
        if (!canProduceFreshWaterWithSteam || !selectedAolDataItem?.aolDataItemProperties.isSurplusSteamUsedForFreshWaterProduction)
            ignoreList.push('freshWaterProducedBySteam');
        if (!canProduceFreshWaterByOsmosis) ignoreList.push('freshWaterProductionByReversedOsmosis');
        if (selectedAolDataItem?.reportType !== NoonReportTypeEnum.Port) ignoreList.push('freshWaterDeliveredFromShore');

        if (hasMissingValues(vm, ignoreList)) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportFreshWaterSectionFactory().createBM(vm);
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!vm.hasInvalidFields) {
                saveSection(data);
            }
        }
    };

    return (
        <>
            {vm && (
                <FreshWaterWrapper>
                    <div>
                        <EngineListWrapper>
                            <ListHeaderStyle>
                                <EngineListRow>
                                    <EngineListHeader className={'first-header-col'}>Production</EngineListHeader>
                                    <EngineListValuesWrapper gridCols={2}>
                                        <EngineListHeader>Volume</EngineListHeader>
                                        <EngineListHeader>Running hours</EngineListHeader>
                                    </EngineListValuesWrapper>
                                </EngineListRow>
                            </ListHeaderStyle>
                            <EngineListRow>
                                <div title="By evaporator connected to ME cooling water system">Evaporator - ME cooling</div>
                                <EngineListValuesWrapper gridCols={2}>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'number'}
                                        unit={'m3'}
                                        name={'freshWaterEvaporatorProduction1'}
                                        value={vm.freshWaterEvaporatorProduction1}
                                        onChange={onInputChange}
                                        min={0}
                                        max={50}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'time'}
                                        name={'freshWaterEvaporatorRunningHours1'}
                                        value={vm.freshWaterEvaporatorRunningHours1}
                                        dataObject={vm}
                                        onTimeInputChange={onTimeInputChange}
                                        min={0}
                                        max={selectedAolGroup?.aolHours}
                                    />
                                </EngineListValuesWrapper>
                            </EngineListRow>
                            <EngineListRow>
                                <div title="By evaporator connected to auxiliary boilers">Evaporator - aux. boilers</div>
                                <EngineListValuesWrapper gridCols={2}>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'number'}
                                        unit={'m3'}
                                        name={'freshWaterEvaporatorProduction2'}
                                        value={vm.freshWaterEvaporatorProduction2}
                                        onChange={onInputChange}
                                        min={0}
                                        max={50}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'time'}
                                        name={'freshWaterEvaporatorRunningHours2'}
                                        value={vm.freshWaterEvaporatorRunningHours2}
                                        dataObject={vm}
                                        onTimeInputChange={onTimeInputChange}
                                        min={0}
                                        max={selectedAolGroup?.aolHours}
                                    />
                                </EngineListValuesWrapper>
                            </EngineListRow>
                        </EngineListWrapper>
                    </div>
                    <div>
                        {canProduceFreshWaterWithSteam && selectedAolDataItem?.aolDataItemProperties.isSurplusSteamUsedForFreshWaterProduction && (
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'number'}
                                description={'Of which the following is produced by surplus steam'}
                                name={'freshWaterProducedBySteam'}
                                value={vm.freshWaterProducedBySteam}
                                onChange={onInputChange}
                                unit={'m3'}
                                min={0}
                                max={50}
                            />
                        )}
                        {canProduceFreshWaterByOsmosis && (
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'number'}
                                description={'By reversed osmosis'}
                                name={'freshWaterProductionByReversedOsmosis'}
                                value={vm.freshWaterProductionByReversedOsmosis}
                                onChange={onInputChange}
                                unit={'m3'}
                                min={0}
                                max={100}
                            />
                        )}
                    </div>
                    {selectedAolDataItem?.reportType === NoonReportTypeEnum.Port && (
                        <div>
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'number'}
                                description={'Total quantity delivered from shore'}
                                name={'freshWaterDeliveredFromShore'}
                                value={vm.freshWaterDeliveredFromShore}
                                onChange={onInputChange}
                                unit={'m3'}
                                min={0}
                                max={2000}
                            />
                        </div>
                    )}
                    <div>
                        <EngineListWrapper>
                            <ListHeaderStyle>
                                <EngineListRow>
                                    <EngineListHeader className={'first-header-col'}>Consumption</EngineListHeader>
                                    <EngineListHeader>Volume</EngineListHeader>
                                </EngineListRow>
                            </ListHeaderStyle>
                            <EngineListRow>
                                <div>Domestic use</div>
                                <EngineListValuesWrapper gridCols={1}>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'number'}
                                        unit={'m3'}
                                        name={'freshWaterUsedForDomesticUse'}
                                        value={vm.freshWaterUsedForDomesticUse}
                                        onChange={onInputChange}
                                        min={0}
                                        max={50}
                                    ></OdfjellInputComponent>
                                </EngineListValuesWrapper>
                            </EngineListRow>
                            <EngineListRow>
                                <div>Tank preparation</div>
                                <EngineListValuesWrapper gridCols={1}>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'number'}
                                        unit={'m3'}
                                        name={'freshWaterUsedForTankCleaning'}
                                        value={vm.freshWaterUsedForTankCleaning}
                                        onChange={onInputChange}
                                        min={0}
                                        max={1000}
                                    ></OdfjellInputComponent>
                                </EngineListValuesWrapper>
                            </EngineListRow>
                        </EngineListWrapper>
                    </div>

                    {hasEmptyValues && <ErrorText>You must complete the section before saving.</ErrorText>}
                </FreshWaterWrapper>
            )}
        </>
    );
};

export default FreshWaterSectionComponent;
